import { useEffect, useRef } from "react";
import { Star, BellOff, Bell, ChevronDown, ChevronUp } from "lucide-react";
import type { MatchInfo, MatchSnapshot } from "@/lib/matches";
import { trackEvent } from "@/lib/audit";

interface Props {
  matches: MatchInfo[];
  snapshots: Record<string, MatchSnapshot>;
  activeId: string;
  onSelect: (id: string) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  followed: string[];
  onToggleFollow: (id: string) => void;
  muted: boolean;
  onToggleMuted: () => void;
  isDemo?: boolean;
}

const eventGlyph = (t?: MatchSnapshot["recentEvent"]) => {
  if (!t) return null;
  if (t.type === "goal") return "⚽";
  if (t.type === "penalty") return "🔥";
  if (t.type === "shot") return "🎯";
  return "⚡";
};

function formatKickoff(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return time;
  const day = d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
  return `${day} ${time}`;
}

export function GlobalScoreBar({
  matches,
  snapshots,
  activeId,
  onSelect,
  expanded,
  onToggleExpanded,
  followed,
  onToggleFollow,
  muted,
  onToggleMuted,
  isDemo = false,
}: Props) {
  const trackedRef = useRef(false);
  useEffect(() => {
    if (!trackedRef.current) {
      trackedRef.current = true;
      void trackEvent("global_score_bar_interaction", { action: "mounted", count: matches.length });
    }
  }, [matches.length]);

  const liveCount = matches.filter((m) => snapshots[m.id]?.status === "LIVE").length;
  const label = isDemo
    ? `${matches.length} Partidos simulados`
    : `${liveCount} Partidos en Vivo`;

  // Collapsed pill
  if (!expanded) {
    return (
      <div className="border-b border-border/40 glass">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-1.5 md:px-6">
          <button
            onClick={onToggleExpanded}
            className="group flex items-center gap-2 rounded-full border border-border/50 bg-background/40 px-3 py-1 text-[11px] font-bold transition hover:border-primary/60 hover:bg-primary/10"
            aria-label="Expandir partidos"
          >
            <span className="text-sm leading-none">⚽</span>
            <span className="tabular-nums">{label}</span>
            <ChevronDown className="h-3 w-3 opacity-70 transition group-hover:translate-y-[1px]" />
          </button>
          <button
            onClick={onToggleMuted}
            aria-label={muted ? "Activar alertas" : "Silenciar alertas"}
            title={muted ? "Activar alertas" : "Silenciar alertas"}
            className="flex h-7 w-7 items-center justify-center rounded-full glass text-muted-foreground hover:bg-accent"
          >
            {muted ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full border-b border-border/40 glass" aria-label="Multi-match hub">
      <div className="mx-auto flex max-w-7xl items-center gap-2 px-3 py-2 md:px-6">
        <div className="flex flex-1 items-stretch gap-2 overflow-x-auto scrollbar-hide">
          {matches.map((m) => {
            const s = snapshots[m.id];
            if (!s) return null;
            const active = m.id === activeId;
            const glyph = eventGlyph(s.recentEvent);
            const isFollowed = followed.includes(m.id);
            return (
              <div
                key={m.id}
                className={`group relative shrink-0 rounded-xl border px-3 py-1.5 text-left transition-all duration-200 ${
                  active
                    ? "border-primary/60 bg-primary/10 shadow-[0_0_24px_-6px_oklch(0.58_0.24_27/0.6)]"
                    : "border-border/50 hover:border-border hover:bg-accent/30"
                }`}
              >
                <button
                  onClick={() => {
                    onSelect(m.id);
                    void trackEvent("global_score_bar_interaction", { action: "select", matchId: m.id });
                  }}
                  className="flex items-center gap-2.5 outline-none"
                  aria-pressed={active}
                  aria-label={`${m.homeCode} vs ${m.awayCode}`}
                >
                  <div className="flex flex-col items-center leading-none">
                    <span className="text-lg leading-none" aria-hidden>{m.homeFlag}</span>
                    <span className="mt-0.5 text-[9px] font-bold text-muted-foreground">{m.homeCode}</span>
                  </div>
                  <span className="text-base font-black tracking-tight tabular-nums px-0.5">
                    {s.homeScore}<span className="text-muted-foreground/60 mx-1">-</span>{s.awayScore}
                  </span>
                  <div className="flex flex-col items-center leading-none">
                    <span className="text-lg leading-none" aria-hidden>{m.awayFlag}</span>
                    <span className="mt-0.5 text-[9px] font-bold text-muted-foreground">{m.awayCode}</span>
                  </div>
                </button>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      s.status === "LIVE"
                        ? "bg-primary animate-pulse-dot"
                        : s.status === "HT"
                        ? "bg-amber-400"
                        : s.status === "SCHEDULED"
                        ? "bg-blue-400"
                        : "bg-muted-foreground"
                    }`}
                  />
                  <span className="text-[11px] font-black tabular-nums tracking-wide text-foreground/80">
                    {s.status === "LIVE"
                      ? `${s.minute}'`
                      : s.status === "SCHEDULED"
                      ? formatKickoff(s.kickoff)
                      : s.status}
                  </span>
                  {glyph && <span className="ml-1 text-[12px] animate-fade-in" aria-hidden>{glyph}</span>}
                  <button
                    onClick={() => onToggleFollow(m.id)}
                    aria-label={isFollowed ? "Dejar de seguir" : "Seguir este partido"}
                    title={isFollowed ? "Dejar de seguir" : "Seguir este partido"}
                    className={`ml-auto rounded-full p-0.5 transition ${
                      isFollowed ? "text-amber-400" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Star className="h-3 w-3" fill={isFollowed ? "currentColor" : "none"} />
                  </button>
                </div>
                {active && (
                  <span className="absolute -bottom-px left-3 right-3 h-[2px] rounded-full bg-gradient-to-r from-transparent via-primary to-transparent" />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            onClick={onToggleMuted}
            aria-label={muted ? "Activar alertas" : "Silenciar alertas"}
            title={muted ? "Activar alertas" : "Silenciar alertas"}
            className="flex h-7 w-7 items-center justify-center rounded-full glass text-muted-foreground hover:bg-accent"
          >
            {muted ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={onToggleExpanded}
            aria-label="Colapsar partidos"
            title="Colapsar"
            className="flex h-7 w-7 items-center justify-center rounded-full glass text-muted-foreground hover:bg-accent"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
